import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as EnableIcon,
  Cancel as DisableIcon,
  Block as SuspendIcon,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {
  fetchTenants,
  createTenant,
  updateTenant,
  deleteTenant,
  enableTenant,
  disableTenant,
  suspendTenant,
  uploadTenantLogo,
  uploadTenantFavicon,
  getTenantLogoUrl,
  getTenantFaviconUrl,
  Tenant,
  TenantStatus,
  statusLabels,
  CreateTenantDto,
  UpdateTenantDto,
} from "../../api/tenant";
import {useAuth} from "../../contexts/AuthContext";
import {tenantThemes} from "../../themes/tenantThemes";
import ThemeSelector from "../../components/common/ThemeSelector";
import FileUpload from "../../components/common/FileUpload";

const TenantListPage: React.FC = () => {
  const {t} = useTranslation();
  const {user: currentUser} = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({open: false, message: "", severity: "success"});

  // Form states
  const [formData, setFormData] = useState<Partial<CreateTenantDto>>({
    name: "",
    theme: "default",
    status: TenantStatus.ACTIVE,
    description: "",
    contactEmail: "",
    contactPhone: "",
    address: "",
  });

  // File upload states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const [uploadingFavicon, setUploadingFavicon] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>(undefined);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await fetchTenants();
      setTenants(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("Failed to load tenants"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTenantFilePreviews = async (tenantId: string) => {
    const [logoUrl, faviconUrl] = await Promise.all([getFileUrl(tenantId, "logo"), getFileUrl(tenantId, "favicon")]);
    setLogoPreviewUrl(logoUrl ?? undefined);
    setFaviconPreviewUrl(faviconUrl ?? undefined);
  };

  const handleOpenDialog = (tenant?: Tenant) => {
    if (tenant) {
      setSelectedTenant(tenant);
      setFormData({
        name: tenant.name,
        theme: tenant.theme,
        status: tenant.status,
        description: tenant.description || "",
        contactEmail: tenant.contactEmail || "",
        contactPhone: tenant.contactPhone || "",
        address: tenant.address || "",
      });
      void loadTenantFilePreviews(tenant.id);
    } else {
      setSelectedTenant(null);
      setFormData({
        name: "",
        theme: "default",
        status: TenantStatus.ACTIVE,
        description: "",
        contactEmail: "",
        contactPhone: "",
        address: "",
      });
      setLogoPreviewUrl(undefined);
      setFaviconPreviewUrl(undefined);
    }
    setOpenEditDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setSelectedTenant(null);
    setLogoPreviewUrl(undefined);
    setFaviconPreviewUrl(undefined);
  };

  const handleSubmit = async () => {
    try {
      if (selectedTenant) {
        await updateTenant(selectedTenant.id, formData as UpdateTenantDto);
        setSnackbar({
          open: true,
          message: t("Tenant updated successfully"),
          severity: "success",
        });
      } else {
        await createTenant(formData as CreateTenantDto);
        setSnackbar({
          open: true,
          message: t("Tenant created successfully"),
          severity: "success",
        });
      }
      handleCloseDialog();
      loadTenants();
    } catch (error) {
      let errorMessage = t("Failed to save tenant");
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const handleDelete = async (tenant: Tenant) => {
    if (window.confirm(t("Are you sure you want to delete this tenant?"))) {
      try {
        await deleteTenant(tenant.id);
        setSnackbar({
          open: true,
          message: t("Tenant deleted successfully"),
          severity: "success",
        });
        loadTenants();
      } catch (error) {
        let errorMessage = t("Failed to delete tenant");
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    }
  };

  const handleStatusChange = async (tenant: Tenant, status: TenantStatus) => {
    try {
      switch (status) {
        case TenantStatus.ACTIVE:
          await enableTenant(tenant.id);
          break;
        case TenantStatus.INACTIVE:
          await disableTenant(tenant.id);
          break;
        case TenantStatus.SUSPENDED:
          await suspendTenant(tenant.id);
          break;
      }
      setSnackbar({
        open: true,
        message: t("Tenant status updated successfully"),
        severity: "success",
      });
      loadTenants();
    } catch (error) {
      let errorMessage = t("Failed to update tenant status");
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const getStatusColor = (status: TenantStatus) => {
    switch (status) {
      case TenantStatus.ACTIVE:
        return "success";
      case TenantStatus.INACTIVE:
        return "warning";
      case TenantStatus.SUSPENDED:
        return "error";
      default:
        return "default";
    }
  };

  const handleLogoUpload = async (tenantId: string, file: File) => {
    try {
      setUploadLoading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append("logo", file);

      await uploadTenantLogo(tenantId, formData);

      await loadTenants();
      const latestLogoUrl = await getFileUrl(tenantId, "logo");
      setLogoPreviewUrl(latestLogoUrl ?? undefined);
      setUploadLoading(false);
      setUploadError(null);
      setSnackbar({
        open: true,
        message: t("Logo uploaded successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error uploading logo:", error);
      setUploadError("Failed to upload logo");
      setUploadLoading(false);
      setSnackbar({
        open: true,
        message: t("Failed to upload logo"),
        severity: "error",
      });
    }
  };

  const handleFaviconUpload = async (tenantId: string, file: File) => {
    try {
      setUploadLoading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append("favicon", file);

      await uploadTenantFavicon(tenantId, formData);

      await loadTenants();
      const latestFaviconUrl = await getFileUrl(tenantId, "favicon");
      setFaviconPreviewUrl(latestFaviconUrl ?? undefined);
      setUploadLoading(false);
      setUploadError(null);
      setSnackbar({
        open: true,
        message: t("Favicon uploaded successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error uploading favicon:", error);
      setUploadError("Failed to upload favicon");
      setUploadLoading(false);
      setSnackbar({
        open: true,
        message: t("Failed to upload favicon"),
        severity: "error",
      });
    }
  };

  const getFileUrl = async (tenantId: string, fileType: "logo" | "favicon"): Promise<string | null> => {
    try {
      if (fileType === "logo") {
        const response = await getTenantLogoUrl(tenantId);
        return response.url;
      } else {
        const response = await getTenantFaviconUrl(tenantId);
        return response.url;
      }
    } catch (error) {
      console.error(`Error getting ${fileType} URL:`, error);
      return null;
    }
  };

  const renderLogo = (tenant: Tenant) => {
    if (!tenant.logoPath) {
      return <Avatar sx={{width: 40, height: 40}}>T</Avatar>;
    }

    return (
      <Avatar
        src={`https://shorekart-smartfarm.s3.ap-south-1.amazonaws.com/${tenant.logoPath}`}
        alt={`${tenant.name} logo`}
        sx={{width: 40, height: 40}}
        onError={(e) => {
          // Fallback to text avatar if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML =
              '<div style="width: 40px; height: 40px; border-radius: 50%; background: #1976d2; color: white; display: flex; align-items: center; justify-content: center; font-size: 16px;">T</div>';
          }
        }}
      />
    );
  };

  const renderFavicon = (tenant: Tenant) => {
    if (!tenant.faviconPath) {
      return <Avatar sx={{width: 24, height: 24}}>F</Avatar>;
    }

    return (
      <Avatar
        src={`https://shorekart-smartfarm.s3.ap-south-1.amazonaws.com/${tenant.faviconPath}`}
        alt={`${tenant.name} favicon`}
        sx={{width: 24, height: 24}}
        onError={(e) => {
          // Fallback to text avatar if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML =
              '<div style="width: 24px; height: 24px; border-radius: 50%; background: #1976d2; color: white; display: flex; align-items: center; justify-content: center; font-size: 12px;">F</div>';
          }
        }}
      />
    );
  };

  const handleLogoFileChange = (file: File) => {
    setLogoFile(file);
    if (selectedTenant) {
      handleLogoUpload(selectedTenant.id, file);
    }
  };

  const handleFaviconFileChange = (file: File) => {
    setFaviconFile(file);
    if (selectedTenant) {
      handleFaviconUpload(selectedTenant.id, file);
    }
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading tenants...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t("Tenant Management")}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          {t("Add Tenant")}
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("Name")}</TableCell>
                <TableCell>{t("Status")}</TableCell>
                <TableCell>{t("Theme")}</TableCell>
                <TableCell>{t("Contact Email")}</TableCell>
                <TableCell>{t("Created")}</TableCell>
                <TableCell>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{tenant.name}</Typography>
                    {tenant.description && (
                      <Typography variant="caption" color="text.secondary">
                        {tenant.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={statusLabels[tenant.status]} color={getStatusColor(tenant.status) as any} size="small" />
                  </TableCell>
                  <TableCell>{tenant.theme}</TableCell>
                  <TableCell>{tenant.contactEmail || "-"}</TableCell>
                  <TableCell>{new Date(tenant.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title={t("Edit")}>
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(tenant)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      {tenant.status !== TenantStatus.ACTIVE && (
                        <Tooltip title={t("Enable")}>
                          <IconButton size="small" color="success" onClick={() => handleStatusChange(tenant, TenantStatus.ACTIVE)}>
                            <EnableIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {tenant.status === TenantStatus.ACTIVE && (
                        <>
                          <Tooltip title={t("Disable")}>
                            <IconButton size="small" color="warning" onClick={() => handleStatusChange(tenant, TenantStatus.INACTIVE)}>
                              <DisableIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("Suspend")}>
                            <IconButton size="small" color="error" onClick={() => handleStatusChange(tenant, TenantStatus.SUSPENDED)}>
                              <SuspendIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      <Tooltip title={t("Delete")}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(tenant)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTenant ? t("Edit Tenant") : t("Add Tenant")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label={t("Name")}
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              fullWidth
              required
            />

            <TextField
              label={t("Description")}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              fullWidth
              multiline
              rows={3}
            />

            <ThemeSelector value={formData.theme} onChange={(theme) => setFormData({...formData, theme: theme})} showPreview={true} />

            <FormControl fullWidth>
              <InputLabel>{t("Status")}</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value as TenantStatus})}
                label={t("Status")}>
                <MenuItem value={TenantStatus.ACTIVE}>{statusLabels[TenantStatus.ACTIVE]}</MenuItem>
                <MenuItem value={TenantStatus.INACTIVE}>{statusLabels[TenantStatus.INACTIVE]}</MenuItem>
                <MenuItem value={TenantStatus.SUSPENDED}>{statusLabels[TenantStatus.SUSPENDED]}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t("Contact Email")}
              value={formData.contactEmail}
              onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
              fullWidth
              type="email"
            />

            <TextField
              label={t("Contact Phone")}
              value={formData.contactPhone}
              onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
              fullWidth
            />

            <TextField
              label={t("Address")}
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              fullWidth
              multiline
              rows={2}
            />

            <FileUpload
              label={t("Logo")}
              accept="image/*"
              onFileSelect={handleLogoFileChange}
              disabled={uploadLoading}
              currentFileUrl={logoPreviewUrl}
              currentFileName={selectedTenant?.logoPath?.split("/").pop()}
              showPreview={true}
              previewSize={120}
            />

            <FileUpload
              label={t("Favicon")}
              accept="image/*"
              onFileSelect={handleFaviconFileChange}
              disabled={uploadLoading}
              currentFileUrl={faviconPreviewUrl}
              currentFileName={selectedTenant?.faviconPath?.split("/").pop()}
              showPreview={true}
              previewSize={32}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedTenant ? t("Update") : t("Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({...snackbar, open: false})}>
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantListPage;
